<template>
  <v-card :outlined="outlined" :flat="!outlined" class="pa-2 d-flex align-center">
    <div class="chip-wrap overflow-hidden flex-grow-1">
      <div class="chip-wrap__inner overflow-auto">
        <template v-if="isFiltering">
          <template v-for="item in data">
            <v-chip
              v-if="isSet(item.value)"
              :key="item.model"
              :disabled="disabled"
              small
              color="accent"
              :close="!(item.clearable === 'false' || item.clearable === false)"
              class="text-caption d-inline-flex align-center py-1 mr-2"
              @click:close="onChipClose(item)"
            >
              <span class="font-weight-bold">{{ item.label }}</span>
              <v-divider vertical class="mx-2" style="border-color: white !important;" />
              <span>{{
                item.options
                  ? getKeyFromOptionValue(item.value, item.options)
                  : item.format
                  ? formatValue(item.value, item.format)
                  : item.value
              }}</span>
            </v-chip>
          </template>
        </template>
        <div v-else class="text-body-2">{{ emptyLabel }}</div>
      </div>
    </div>
    <div v-if="$slots.default" class="d-flex">
      <v-divider vertical class="mx-2" />
      <slot />
    </div>
  </v-card>
</template>
<script>
import { format as fnsFormat } from 'date-fns'
import isSet from '@/utils/is-set'
import setPlural from '@/utils/set-plural'
import { formatDate, formatTime } from '@/utils/date'

export default {
  name: 'FilterChips',
  props: {
    /**
     * Required keys in each object: label[string], model[string] and value[any].
     * Optional keys are: options[array] and format[string].
     */
    data: {
      type: Array,
      required: true,
      validator(data) {
        return data.every(item => {
          return 'label' in item && 'model' in item && 'value' in item
        })
      },
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    emptyLabel: {
      type: String,
      default: 'No filters selected',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isFiltering() {
      return this.data.some(({ value, allowNull }) => isSet(value) || (value === null && allowNull))
    },
  },
  methods: {
    isSet,
    formatValue(value, format) {
      if (!format) return value

      switch (format) {
        case 'datetime':
          try {
            return `${formatDate(value, { separator: '-' })} ${formatTime(value)}`
          } catch (e) {
            return value
          }
        case 'daterange':
          try {
            return value.join('~')
          } catch (e) {
            return value
          }
        case 'month-year':
          try {
            return fnsFormat(new Date(value), 'MMMM y')
          } catch (e) {
            return value
          }
        case 'date':
          try {
            return formatDate(value, { separator: '-' })
          } catch (e) {
            return value
          }
        default:
          return value
      }
    },
    getKeyFromOptionValue(value, options) {
      if (!options) return value

      if (Array.isArray(value)) {
        const option = options.find(opt => opt.value === value[0])
        const key = option ? option.key : value[0]
        const totalOtherValues = value.length - 1
        if (totalOtherValues) {
          return `${key} + ${totalOtherValues} ${setPlural(totalOtherValues, 'other')}`
        } else {
          return key
        }
      }

      const option = options.find(opt => opt.value === value)
      return option ? option.key : value
    },
    onChipClose(item) {
      this.$emit('clear-chip', item)
    },
  },
}
</script>

<style lang="scss" scoped>
.chip-wrap {
  position: relative;
  white-space: nowrap;

  &__inner {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &:after {
    content: '';
    width: 8px;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    background-image: linear-gradient(90deg, transparent, #fff);
  }
}
</style>
